import { useState, useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { DomHandler } from 'primereact/utils'

/**
 * Custom hook for managing row editing in StaticGrid.
 *
 * @param {Object} params - Hook configuration
 * @param {string} params.dataKey - Primary key field name
 * @param {Array} params.columnDefinitions - Column definitions array
 * @param {Array} params.localData - Local grid data
 * @param {Function} params.setLocalData - Function to update local data
 * @param {Object} params.originalData - Original data for comparison
 * @param {Array} params.data - Current grid data
 * @param {Object} params.selectedChannels - Selected channels state
 * @param {Function} params.setSelectedChannels - Function to update selected channels
 * @param {Function} params.validateData - Validation function (rowData) => errors object
 * @param {Function} params.onSave - Save handler (rowData) => void
 * @param {Function} params.startEditing - Redux action to dispatch on edit start
 * @param {Function} params.stopEditing - Redux action to dispatch on edit stop
 * @param {Function} params.generateNewRowKey - Optional custom key generator (dataKey, localData, originalData) => key
 * @param {Function} params.onRowCancelCustom - Optional custom cancel logic (rowData, data, selectedChannels, setSelectedChannels) => void
 * @param {Function} params.removeFromEditingSet - Callback to remove row key from editing Set
 * @param {Object} params.toastRef - Optional toast ref for showing validation errors
 * @returns {Object} Row editing utilities
 */
export const useRowEditing = ({
    dataKey,
    columnDefinitions,
    localData,
    setLocalData,
    originalData,
    data,
    selectedChannels,
    setSelectedChannels,
    validateData,
    onSave,
    startEditing,
    stopEditing,
    generateNewRowKey,
    onRowCancelCustom,
    onRowEditInitCustom,
    toastRef,
    editingRowKey,
    setEditingRowKey,
    removeFromEditingSet
}) => {
    const [validationErrors, setValidationErrors] = useState({})
    const isEditingRef = useRef(false)
    const dispatch = useDispatch()

    // Cleanup on unmount - ensures stopEditing is called if component unmounts while editing
    useEffect(() => {
        return () => {
            if (isEditingRef.current && stopEditing) {
                dispatch(stopEditing())
            }
        }
    }, [stopEditing, dispatch])

    const openNewRowInEditMode = (initialData = {}) => {
        const newRow = {}

        // Use custom key generator if provided, otherwise use default logic
        if (generateNewRowKey) {
            newRow[dataKey] = generateNewRowKey(dataKey, localData, originalData)
        } else {
            // Default: generate UUID
            newRow[dataKey] = crypto.randomUUID()
        }

        columnDefinitions.forEach((col) => {
            if (col.mapsToField !== dataKey) {
                newRow[col.mapsToField] = null
            }
        })

        // Merge with initial data (overrides nulls)
        const rowWithData = { ...newRow, ...initialData }

        // Set editing key immediately to prevent useGridDataSync from resetting localData
        setEditingRowKey(rowWithData[dataKey])
        // Note: Don't call startEditing here - onRowEditInit will handle it when the programmatic click triggers

        setLocalData((prevLocalData) => ([rowWithData, ...prevLocalData]))

        requestAnimationFrame(() => {
            DomHandler.find(document.body, '.p-row-editor-init')[0].click()
        }, 0)
    }

    const onRowEditInit = (e) => {
        isEditingRef.current = true
        setEditingRowKey(e.data[dataKey])
        if (startEditing) {
            dispatch(startEditing())
        }
        // Call custom onRowEditInit if provided
        if (onRowEditInitCustom) {
            onRowEditInitCustom(e)
        }
    }

    const onRowEditSave = (e) => {
        // Editing state is cleared in rowEditValidator only when validation passes
    }

    const rowEditValidator = (rowData) => {
        const errors = validateData(rowData)
        if (Object.keys(errors).length > 0) {
            const rowIndex = localData.findIndex(row => row[dataKey] === rowData[dataKey])
            setValidationErrors({
                [rowIndex]: errors
            })
            // Show toast if ref is provided
            if (toastRef?.current) {
                toastRef.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: Object.values(errors).join(', ')
                })
            }
            return false
        }
        else {
            // Validation passed - clear editing state
            isEditingRef.current = false
            const rowKey = rowData[dataKey]
            setEditingRowKey(null)
            // Remove this specific row from the editing Set
            if (removeFromEditingSet && rowKey) {
                removeFromEditingSet(rowKey)
            }
            if (stopEditing) {
                dispatch(stopEditing())
            }
            onSave(rowData)
            setValidationErrors({})
            const updatedData = [...localData]
            const rowIndex = updatedData.findIndex(row => row[dataKey] === rowData[dataKey])
            if (rowIndex !== -1) {
                updatedData[rowIndex] = rowData
                setLocalData(updatedData)
            }
            return true
        }
    }

    const onRowEditCancel = (e) => {
        isEditingRef.current = false
        const rowKey = e.data[dataKey]
        setEditingRowKey(null)
        // Remove this specific row from the editing Set
        if (removeFromEditingSet && rowKey) {
            removeFromEditingSet(rowKey)
        }
        const rowIndex = localData.findIndex(row => row[dataKey] === e.data[dataKey])
        const existingItem = data.find(row => row[dataKey] === e.data[dataKey])
        setValidationErrors({})
        // Execute custom cancel logic if provided (for both new and existing rows)
        if (onRowCancelCustom) {
            onRowCancelCustom(e.data, data, selectedChannels, setSelectedChannels)
        }
        if (!existingItem) {
            // Delete the item from localData (new row that was never saved)
            if (rowIndex !== -1) {
                const updatedData = localData.filter((_, index) => index !== rowIndex)
                setLocalData(updatedData)
            }        
        }

        if (stopEditing) {
            dispatch(stopEditing())
        }
    }

    const updateRow = (rowIndex, updatedRow) => {
        const updatedData = [...localData]
        updatedData[rowIndex] = updatedRow
        setLocalData(updatedData)
    }

    // Update a specific field in a row and trigger re-render
    // Creates a new row object to ensure React detects the change
    const updateRowField = (rowIndex, field, value) => {
        setLocalData(prevData => {
            if (prevData[rowIndex]) {
                // Create a new object to ensure React detects the change
                const newRow = { ...prevData[rowIndex], [field]: value }
                const newData = [...prevData]
                newData[rowIndex] = newRow
                return newData
            }
            return prevData
        })
    }

    // Update multiple fields in a row at once (avoids multiple state updates)
    const updateRowFields = (rowIndex, fieldsObj) => {
        setLocalData(prevData => {
            if (prevData[rowIndex]) {
                const newRow = { ...prevData[rowIndex], ...fieldsObj }
                const newData = [...prevData]
                newData[rowIndex] = newRow
                return newData
            }
            return prevData
        })
    }


    return {
        validationErrors,
        editingRowKey,
        openNewRowInEditMode,
        onRowEditInit,
        onRowEditSave,
        rowEditValidator,
        onRowEditCancel,
        updateRow,
        updateRowField,
        updateRowFields
    }
}
